import { ArrowLeftIcon, Button, LoadSpinner } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import React, { useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { ReactComponent as AirbnbLogo } from '@/assets/icons/airbnb.svg'
import { BottomSheet, History } from '@/components'
import { RecordType } from '@/graphql/queries/fieldHistoryLogs/fieldHistoryLogs.queries'
import { useMobile, useToggle } from '@/hooks'
import useTicket from '@/hooks/useTicket/useTicket'
import { Status, Type } from '@/types/Ticket'

import { CloseTicket } from './CloseTicket'
import { DetailsSection } from './DetailsSection/DetailsSection'
import { TicketDetailTab, TicketDetailTabs } from './TicketDetailTabs'
import { UpdatesSection } from './UpdatesSection'

const ParentTicketLink = ({
  parentTicket,
  parentTicketId,
}: {
  parentTicket: { id: number; subject: string }
  parentTicketId: number
}) => {
  const navigate = useNavigate()
  return (
    <p
      className="mb-2 cursor-pointer px-6 text-p-sm text-tertiary underline md:px-0"
      onClick={() => navigate(`/tickets/${parentTicketId}`)}
      role="presentation"
    >
      &larr; Sub-ticket of #{parentTicketId} - {parentTicket.subject}
    </p>
  )
}

export function Ticket() {
  const [selectedTab, setSelectedTab] = useState<TicketDetailTab>(TicketDetailTab.Details)
  const { ticketId } = useParams<{ ticketId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const backTo = (location.state as { from?: string } | null)?.from ?? '/tickets'
  const { isTablet, isMobile } = useMobile()
  const { isShown: modalShown, toggle: modalToggle } = useToggle()
  const { isShown: ticketHistoryModalShown, toggle: ticketHistoryModalToggle } = useToggle()
  const showTabView = isTablet || isMobile

  const { data, isLoading, isError, error, refetch } = useTicket(ticketId)

  const ticketData = data?.ticket

  const handleCloseTicket = () => {
    modalToggle()
  }
  const { home, ticketType } = ticketData || {}
  const { landingDirect, isAutopilot } = home?.property || { landingDirect: false, isAutopilot: false }

  const handledByDirectParner =
    !!landingDirect && !isAutopilot && (ticketType === Type.MAINTENANCE || ticketType === Type.HOME)

  const isTicketClosed = ticketData?.internalTicketStatus === Status.CLOSED

  return (
    <div className="flex w-full flex-col pb-6 pt-4 md:px-6">
      {ticketData?.parentTicket && ticketData?.parentTicketId && (
        <ParentTicketLink parentTicket={ticketData.parentTicket} parentTicketId={ticketData.parentTicketId} />
      )}
      <div className="mb-2  flex w-full justify-between px-6 md:mb-6 md:items-center md:px-0">
        <div className="flex flex-col md:flex-row md:items-center">
          <button
            className="mr-4 flex h-10 w-10 items-center rounded-full md:mb-0 md:justify-center md:bg-grey-100"
            onClick={() => navigate(backTo)}
          >
            <ArrowLeftIcon className="h-6" />
          </button>
          <div className="flex items-center gap-3">
            <StyledHeader className="text-header-lg">{ticketData?.subject}</StyledHeader>
            {ticketData?.externalSource && (
              <div className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 shadow-md ring-1 ring-gray-200">
                <AirbnbLogo className="h-4 w-auto" />
              </div>
            )}
          </div>
        </div>
        <div className="flex">
          {!isTicketClosed && (
            <Button
              className="hidden bg-grey-100 md:block"
              onClick={handleCloseTicket}
              size="small"
              variant="destructive"
            >
              Close Ticket
            </Button>
          )}
          <Button className="ml-4 bg-grey-100" onClick={ticketHistoryModalToggle} size="small" variant="secondary">
            View History
          </Button>
        </div>
      </div>
      {handledByDirectParner && (
        <div className="mx-6 mb-2 rounded-lg px-2 py-2 text-semantic-success tag-green-light md:mx-0 md:mb-6 md:px-6">
          {' '}
          This ticket should be managed by the Direct Partner{' '}
        </div>
      )}

      <TicketDetailTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
      <div className="flex w-full flex-1 flex-row px-4 md:px-0">
        {isLoading ? (
          <div className="flex h-48 w-full items-center justify-center ">
            <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
          </div>
        ) : isError ? (
          <div className="flex h-48 w-full flex-col items-center justify-center gap-2 text-semantic-error">
            <p className="font-medium">Failed to load ticket</p>
            <p className="text-sm text-tertiary">{(error as any)?.message || 'Unknown error'}</p>
          </div>
        ) : (
          <>
            {ticketData && (
              <>
                {showTabView ? (
                  <>
                    {selectedTab === TicketDetailTab.Details && (
                      <DetailsSection
                        handleCloseTicket={handleCloseTicket}
                        refetchDetails={refetch}
                        selectedTab={selectedTab}
                        ticketData={ticketData}
                      />
                    )}
                    {selectedTab === TicketDetailTab.Updates && (
                      <UpdatesSection selectedTab={selectedTab} ticketData={ticketData} />
                    )}
                  </>
                ) : (
                  <>
                    <DetailsSection
                      handleCloseTicket={handleCloseTicket}
                      refetchDetails={refetch}
                      selectedTab={selectedTab}
                      ticketData={ticketData}
                    />
                    <UpdatesSection selectedTab={selectedTab} ticketData={ticketData} />
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
      {ticketData && ticketData.id && (
        <>
          <BottomSheet isVisible={modalShown} onClose={modalToggle}>
            <CloseTicket
              childTickets={ticketData.childTickets}
              refetchDetails={refetch}
              ticketId={ticketData.id}
              toggle={modalToggle}
            />
          </BottomSheet>

          <BottomSheet isVisible={ticketHistoryModalShown} mobileFullHeight={true} onClose={ticketHistoryModalToggle}>
            <History recordId={ticketData.id} recordType={RecordType.TICKETS} />
          </BottomSheet>
        </>
      )}
    </div>
  )
}

const StyledHeader = styled.p`
  @media (max-width: 768px) {
    display: -webkit-box;
    max-width: 100%;
    height: 5rem;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 120%;
  }
`
